/**
 * @fileoverview Marketing Website Features Page
 * @module apps/purchase-web/portals/marketing-website/pages/FeaturesPage
 * @description Comprehensive features showcase for PurchasePuffin e-commerce platform
 *
 * No isMountedRef ceremony — React 19 makes post-unmount setState a silent
 * no-op; see GUIDES/shared/standards/testing/react-19-unmount-semantics.md.
 */

import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MarketingSeo, CTASection, useAnalytics } from '@puffin-core/marketing-shell';
// AAS-I18N-002 ruling 79 — a FACTORY, not a frozen const: the 126 category/feature strings are
// resolved at render so a language switch actually changes them.
import { buildFeatureCategories } from '../data/FeatureCategoriesData';
import { useFeaturesPageSeo } from '../hooks/useFeaturesPageSeo';
import { PURCHASE_MARKETING_ROUTES } from '../constants/purchaseMarketingRoutePaths';

/**
 * Feature category header component
 * @param {Object} props - Component props
 * @param {string} props.id - Category ID for accessibility
 * @param {string} props.title - Category title
 * @param {string} props.description - Category description
 * @param {React.JSX.Element} props.icon - Category icon
 * @returns {React.JSX.Element} Category header
 */
const FeatureCategoryHeader = ({ id, title, description, icon }) => (
  <div className="section-header-icon">
    <div className="section-icon-box">{icon}</div>
    <div className="section-title-group">
      <h2 id={`${id}-heading`} className="category-title">
        {title}
      </h2>
      <p className="category-description">{description}</p>
    </div>
  </div>
);

FeatureCategoryHeader.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
};

/**
 * Expandable Feature Item Component
 * @param {Object} props - Component props
 * @param {string} props.name - Feature name
 * @param {string} props.description - Feature description
 * @param {string} props.details - Extended feature details
 * @param {boolean} props.isExpanded - Whether the feature is expanded
 * @param {Function} props.onToggle - Toggle handler
 * @returns {React.JSX.Element} Feature item component
 */
const FeatureItem = ({ name, description, details, isExpanded, onToggle }) => (
  <div
    className={`expandable-card${isExpanded ? ' expandable-card-expanded' : ''}`}
    role="button"
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    }}
    aria-expanded={isExpanded}
  >
    <div className="expandable-card-header">
      <span className="expandable-card-title">{name}</span>
      <span className="expandable-card-toggle" aria-hidden="true">
        {isExpanded ? '\u2212' : '+'}
      </span>
    </div>
    <p className="expandable-card-body">{description}</p>
    {isExpanded && details && (
      <div className="expandable-card-details">
        <p>{details}</p>
      </div>
    )}
  </div>
);

FeatureItem.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  details: PropTypes.string,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

/**
 * Feature category section component
 * @param {Object} props - Component props
 * @param {Object} props.category - Category data
 * @param {Object} props.expandedFeatures - Map of expanded feature states
 * @param {Function} props.onToggleFeature - Toggle handler for features
 * @returns {React.JSX.Element} Category section
 */
const FeatureCategorySection = ({ category, expandedFeatures, onToggleFeature }) => {
  const { id, title, description, icon, features } = category;

  return (
    <section id={id} className="content-section" aria-labelledby={`${id}-heading`}>
      <div className="content-section-container">
        <FeatureCategoryHeader id={id} title={title} description={description} icon={icon} />
        <div className="content-grid">
          {features.map(({ name, description: featureDesc, details }) => (
            <FeatureItem
              key={name}
              name={name}
              description={featureDesc}
              details={details}
              isExpanded={expandedFeatures[`${id}-${name}`] || false}
              onToggle={() => onToggleFeature(id, name)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

FeatureCategorySection.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    icon: PropTypes.node.isRequired,
    features: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        details: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  expandedFeatures: PropTypes.object.isRequired,
  onToggleFeature: PropTypes.func.isRequired,
};

/**
 * Features hero section component
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} Hero section
 */
const FeaturesHero = ({ t }) => (
  <section className="hero-primary" aria-labelledby="features-heading">
    <div className="container">
      <h1 id="features-heading" className="hero-title">
        {t('features.hero.title')}
      </h1>
      <p className="hero-subtitle">{t('features.hero.subtitle')}</p>
    </div>
  </section>
);

FeaturesHero.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Feature navigation link item component
 * @param {Object} props - Component props
 * @param {string} props.id - Category ID
 * @param {string} props.navTitle - Short navigation title
 * @param {boolean} props.isActive - Whether this nav item is active
 * @returns {React.JSX.Element} Navigation item
 */
const FeatureNavItem = ({ id, navTitle, isActive }) => (
  <li>
    <button
      type="button"
      className={`pill-nav-link${isActive ? ' pill-nav-link-active' : ''}`}
      onClick={() => {
        // DOM-TIMER-HYGIENE-001 (Wave 9): anchor scroll-target lookup is the
        // canonical use of getElementById — the section <h2> in another part
        // of the page renders `id={id}`, and React refs can't span sibling
        // components in a single-page layout. Native DOM lookup is correct.
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }}
      aria-current={isActive ? 'true' : undefined}
    >
      {navTitle}
    </button>
  </li>
);

FeatureNavItem.propTypes = {
  id: PropTypes.string.isRequired,
  navTitle: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
};

/**
 * Features navigation component with scroll spy
 * @param {Object} props - Component props
 * @param {string} props.activeSection - Currently active section ID
 * @returns {React.JSX.Element} Navigation section
 */
const FeaturesNav = ({ activeSection }) => {
  const { t } = useTranslation();
  const categories = useMemo(() => buildFeatureCategories(t), [t]);
  return (
    <nav className="pill-nav" aria-label={t('features.nav.ariaLabel')}>
      <div className="container">
        <ul className="pill-nav-list">
          {categories.map(({ id, navTitle }) => (
            <FeatureNavItem key={id} id={id} navTitle={navTitle} isActive={activeSection === id} />
          ))}
        </ul>
      </div>
    </nav>
  );
};

FeaturesNav.propTypes = {
  activeSection: PropTypes.string.isRequired,
};

/**
 * Features content with category sections
 * @param {Object} props - Component props
 * @param {Object} props.expandedFeatures - Map of expanded feature states
 * @param {Function} props.onToggleFeature - Toggle handler for features
 * @returns {React.JSX.Element} Features content
 */
const FeaturesContent = ({ expandedFeatures, onToggleFeature }) => {
  const { t } = useTranslation();
  const categories = useMemo(() => buildFeatureCategories(t), [t]);
  return (
    <div>
      {categories.map((category) => (
        <FeatureCategorySection
          key={category.id}
          category={category}
          expandedFeatures={expandedFeatures}
          onToggleFeature={onToggleFeature}
        />
      ))}
    </div>
  );
};

FeaturesContent.propTypes = {
  expandedFeatures: PropTypes.object.isRequired,
  onToggleFeature: PropTypes.func.isRequired,
};

/**
 * Features Page Component
 *
 * Features:
 * - 6 feature categories with 36 detailed features
 * - Expandable feature cards with extended details
 * - Icon-based visual hierarchy
 * - Pill navigation with scroll spy
 * - CTA section
 *
 * @returns {React.JSX.Element} Features page component
 */
const FeaturesPage = () => {
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [activeSection, setActiveSection] = useState('');
  const { t } = useTranslation();
  const { trackPageView } = useAnalytics();
  const { title, description, canonical, jsonLd, ogImageUrl } = useFeaturesPageSeo();

  useEffect(() => {
    trackPageView('/features', 'Features Page');
  }, [trackPageView]);

  // Scroll spy - detect which section is in view and highlight nav
  useEffect(() => {
    // ⚠️ IDs only — a category `id` is a scroll-spy anchor, not copy, so the identity translator is
    // used and the observer is never rebuilt on a locale change.
    const sectionIds = buildFeatureCategories((key) => key).map((cat) => cat.id);
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sectionIds.forEach((id) => {
      // DOM-TIMER-HYGIENE-001 (Wave 9): IntersectionObserver targets are
      // resolved by DOM id at observer-setup time — sections are nested
      // deep in the page and not reachable by ref. getElementById is the
      // right API for the scroll-spy pattern.
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect.call(observer);
  }, []);

  // Accordion behavior - only one feature expanded at a time
  const toggleFeature = (categoryId, featureName) => {
    const key = `${categoryId}-${featureName}`;
    setExpandedFeatures((prev) => {
      if (prev[key]) {
        return {};
      }
      return { [key]: true };
    });
  };

  return (
    <>
      <MarketingSeo
        title={title}
        description={description}
        canonical={canonical}
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={jsonLd}
      />
      <div className="page-clip-overflow" data-testid="features-page">
        <FeaturesHero t={t} />
        <FeaturesNav activeSection={activeSection} />
        <FeaturesContent expandedFeatures={expandedFeatures} onToggleFeature={toggleFeature} />
        <CTASection
          heading={t('features.cta.heading')}
          subheading={t('features.cta.subheading')}
          primaryAction={t('features.cta.primaryAction')}
          primaryLink={PURCHASE_MARKETING_ROUTES.CONTACT}
          secondaryAction={t('features.cta.secondaryAction')}
          secondaryLink={PURCHASE_MARKETING_ROUTES.HOME}
        />
      </div>
    </>
  );
};

export default FeaturesPage;
